/**
 * ui.tsx — shared primitives for the authed STAFF surfaces (clinician + admin).
 *
 * Owner repo: @thegpservice/gpsclinics-portal. Slice: C1.5 (clinician + admin).
 *
 * WHY: the clinician workspace and admin console share a visual grammar with the
 * patient dashboard (raised cards, a section title, a label/value row, a sign-out
 * link) but sit on the wider page frame. Centralising them keeps the staff
 * surfaces on the same GPS design and avoids re-deriving the same styles. Every
 * value is a `var(--gps-*)` token so light/dark just work.
 */
import { useState, type CSSProperties, type ReactNode } from 'react';
import { useAuth } from '../auth/AuthContext';
import { BASENAME } from '../config';
import { copyToClipboard } from './clipboard';
import { Button, CheckIcon } from '../ui/kit';

// Card-content primitives (SectionTitle / Row / CardGrid) now live in the
// role-neutral `ui/sections.tsx` — the patient surfaces use the same grammar.
// Re-exported here so the existing staff imports (`from '../staff/ui'`) are
// unchanged.
export { SectionTitle, SectionTitleInline, Row, CardGrid } from '../ui/sections';

/**
 * The underlined "Sign out" text button. CURRENTLY UNCONSUMED: the staff
 * surfaces sign out via the header ProfileMenu (ui/chrome.tsx) and the
 * patient Dashboard carries its own inline button pending its rework — the
 * export stays for that rework to reuse rather than re-derive.
 */
export function SignOutButton() {
  const { logout } = useAuth();
  return (
    <button
      type="button"
      onClick={() => logout()}
      style={{ marginLeft: 'auto', background: 'none', border: 0, color: 'var(--gps-link)', font: 'inherit', fontSize: '14px', cursor: 'pointer', textDecoration: 'underline' }}
    >
      Sign out
    </button>
  );
}

/**
 * Build the single-use activation link an invitee opens to set their own
 * password. The `?mode=invite&token=…` shape is consumed by AuthPage. Extracted
 * here because BOTH invite surfaces (the Create-user wizard and the pending-
 * invite "resend" row) mint the exact same link — one helper keeps the query
 * shape and the `encodeURIComponent` in a single place.
 *
 * BASENAME (Clinics-in-Core, 2026-09-07): the link resolves wherever THIS build
 * is served — an admin working inside GPS Core mints it on the Core host, where
 * /auth lives under /clinics. '' when standalone, so that link is unchanged.
 */
export function buildInviteLink(token: string): string {
  return `${window.location.origin}${BASENAME}/auth?mode=invite&token=${encodeURIComponent(token)}`;
}

/** The compact text-link chrome for the `link` CopyLinkButton variant (44px touch target). */
const copyLinkTextStyle: CSSProperties = {
  minHeight: '44px',
  padding: '0 4px',
  background: 'none',
  border: 0,
  color: 'var(--gps-link)',
  font: 'inherit',
  fontSize: '13px',
  fontWeight: 600,
  textDecoration: 'underline',
  cursor: 'pointer',
  whiteSpace: 'nowrap',
};

/**
 * THE COMPACT STAFF ACTION (2026-09-25, CTO on the pharmacy sign-in link panel:
 * "after creating the link I think we're using too much space here … we can
 * show two small buttons once the link is generated").
 *
 * The kit's ghost `Button`, shrunk to its content: a short visible label beside
 * a 16px glyph, 13px type. Laid over `buttonStyle('ghost')`, so it keeps the
 * ghost edge, ink, hover fill (the `gps-btn-ghost` class) and focus ring.
 *
 * ⚠️ IT LOOKS SMALL AND IS STILL A 44×44 TARGET. `minHeight` and `minWidth`
 * hold the hit area at 44px whatever the label (WCAG 2.5.8 asks 24; this estate
 * holds every control at 44, `copyLinkTextStyle` above included). Shrink the
 * padding or the type, never the box.
 *
 * ⚠️ A LABEL THAT CHANGES CHANGES THE WIDTH WITH IT ("Copied", "Creating…"),
 * and a width change re-flows whatever shares the row. A caller whose words
 * change holds the button at its widest words, measured in a browser, with a
 * `minWidth` of its own laid over this one (see `admin/PharmacyLinkAction.tsx`,
 * review 2026-09-25). This style cannot pick that width: it does not know the
 * words.
 *
 * ⚠️ `width: 'auto'` IS LOAD-BEARING: `buttonStyle()` is `width: 100%`, and a
 * 100%-wide child in a wrapping row takes a line to itself (the CLAUDE.md
 * `buttonStyle()` rule). Two of these sit side by side, so neither may be full
 * width.
 *
 * A compact control's short label rarely names its object ("Copy", "Create
 * another"), so a caller adds the rest as visually hidden words
 * (`CompactActionLabel`), never as an `aria-label` — the name then STARTS with
 * the visible words (WCAG 2.5.3) and follows them when they change ("Copied").
 */
export const compactActionStyle: CSSProperties = {
  width: 'auto',
  minWidth: '44px',
  minHeight: '44px',
  padding: '0 12px',
  gap: '6px',
  fontSize: '13px',
  fontWeight: 600,
  lineHeight: 1.2,
  whiteSpace: 'nowrap',
};

/**
 * The words of a compact action: what is SHOWN, then what is only HEARD.
 * `hidden` is appended to the accessible name as visually hidden text, so
 * "Copy" + "pharmacy sign-in link" is announced "Copy pharmacy sign-in link".
 * The leading space is written here, once, so no caller can run the two
 * halves together.
 */
export function CompactActionLabel({ shown, hidden }: { shown: string; hidden?: string }) {
  return (
    <>
      <span>{shown}</span>
      {hidden ? <span className="gps-sr-only">{` ${hidden}`}</span> : null}
    </>
  );
}

/**
 * CopyLinkButton — copies `value` and owns the transient "Copied ✓" state
 * machine (flash the confirmation, then reset after 1.5s). Extracted because the
 * invite wizard and the pending-invite row both duplicated the identical
 * copied-flag + copyToClipboard + setTimeout dance; only the chrome differed, so
 * `variant` picks that (a full ghost `Button`, a compact underlined text link,
 * or since 2026-09-25 the `compact` ghost — see `compactActionStyle`) while the
 * copy behaviour stays in one place.
 */
export function CopyLinkButton({
  value,
  label = 'Copy link',
  variant = 'ghost',
  icon,
  hiddenLabel,
  onCopied,
  style,
}: {
  value: string;
  label?: string;
  /**
   * `compact` (2026-09-25): the small ghost of `compactActionStyle` — a short
   * `label` ("Copy") beside `icon`, turning to a tick and "Copied" on success.
   * Its name should say WHAT is copied: pass `hiddenLabel`.
   */
  variant?: 'ghost' | 'link' | 'compact';
  /**
   * `compact` only: words appended to the accessible name as visually hidden
   * text (see `CompactActionLabel`) — "pharmacy sign-in link" makes the name
   * "Copy pharmacy sign-in link", and "Copied pharmacy sign-in link" once it
   * has worked, so the name always starts with what the button shows.
   */
  hiddenLabel?: string;
  /**
   * An optional DECORATIVE glyph beside the text (2026-09-05) — the deal
   * drawer's "Copy plan link" passes one so its four actions are told apart at
   * a glance; every other call site passes none and is unchanged. Never the
   * label: `label` still carries the accessible name.
   */
  icon?: ReactNode;
  /**
   * Fired AFTER the value actually reached the clipboard, and only then
   * (2026-09-06). Added for the plan link's copy audit — clinic-service wants
   * to know when a human took the link, because a link copied into WhatsApp is
   * a contact attempt that leaves no other trace.
   *
   * ⚠️ THE ORDER IS THE CONTRACT AND IT IS ENFORCED HERE, NOT AT THE CALL SITE.
   * The copy happens first and this runs after it; the button's own "Copied ✓"
   * state does not wait on it, and a throwing callback is not this component's
   * problem to display. Anything expensive or fallible a caller wants to do
   * must be fire-and-forget on their side (see `recordPlanLinkCopied`) — this
   * is a notification that a copy HAPPENED, never a step in making one happen.
   *
   * NOT fired on a FAILED copy. A clipboard write can be refused (an insecure
   * origin with no `execCommand` fallback), and auditing "staff copied the
   * link" when nothing reached the clipboard would put a contact attempt on the
   * record that never occurred.
   */
  onCopied?: () => void;
  style?: CSSProperties;
}) {
  const [copied, setCopied] = useState(false);

  async function handleCopy() {
    const ok = await copyToClipboard(value);
    if (ok) {
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
      onCopied?.();
    }
  }

  /* The copied state drops the glyph for the tick: two icons on one short
     button is noise, and the tick IS the feedback at that moment. */
  const content = copied ? (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
      Copied <CheckIcon size={12} />
    </span>
  ) : icon ? (
    <>
      {icon}
      {label}
    </>
  ) : (
    label
  );
  if (variant === 'link') {
    return (
      <button type="button" onClick={handleCopy} style={{ ...copyLinkTextStyle, ...style }}>
        {content}
      </button>
    );
  }
  if (variant === 'compact') {
    /* The glyph swaps for the tick rather than sitting beside it (the same rule
       as `content` above). The label is its own span so the tick, the words
       and the hidden half line up on the button's flex gap. */
    return (
      <Button type="button" variant="ghost" onClick={handleCopy} style={{ ...compactActionStyle, ...style }}>
        {copied ? <CheckIcon size={14} /> : icon}
        <CompactActionLabel shown={copied ? 'Copied' : label} hidden={hiddenLabel} />
      </Button>
    );
  }
  return (
    <Button type="button" variant="ghost" onClick={handleCopy} style={style}>
      {content}
    </Button>
  );
}
